import React, { useState, useEffect, useMemo } from "react";
import { useTable } from "react-table";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SummaryApi from "../common";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  FaSyncAlt,
  FaFileExcel,
  FaPhoneAlt,
  FaCalendarAlt,
  FaTimes,
  FaDownload,
} from "react-icons/fa";

const THEME = {
  primary: "#172C57",
  primaryLight: "rgba(23, 44, 87, 0.08)",
  primaryHover: "#1E3A6E",
  emerald: "#10B981",
  purple: "#7C3AED",
  red: "#EF4444",
  amber: "#F59E0B",
  blue: "#2563EB",
};

/* ---------- Helper: sum call_duration strings ("HH:MM:SS") ---------- */
const sumCallDurations = (records) => {
  const totalSeconds = (records || []).reduce((acc, rec) => {
    if (!rec || !rec.call_duration) return acc;
    const parts = String(rec.call_duration).split(":");
    const h = parseInt(parts[0], 10) || 0;
    const m = parseInt(parts[1], 10) || 0;
    const s = parseInt(parts[2], 10) || 0;
    return acc + h * 3600 + m * 60 + s;
  }, 0);

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0"
  )}:${String(seconds).padStart(2, "0")}`;
};

const EmployeeReport = () => {
  const [employeeData, setEmployeeData] = useState([]);
  const [teamLeads, setTeamLeads] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filterRange, setFilterRange] = useState("today");

  // Export Modal States
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedExportTeam, setSelectedExportTeam] = useState("all");

  const headers = {
    Authorization: "Bearer sandeep_university_916",
    "Content-Type": "application/json",
  };

  const isValidAadharDate = (aadhar) => {
    return aadhar && aadhar !== "N/A";
  };

  const handlePageReload = () => {
    window.location.reload();
  };

  useEffect(() => {
    const interval = setInterval(() => {
      handlePageReload();
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  /* ============================================================
     openInNewTab — renders HTML tab + Excel/CSV export
     ============================================================ */
  const openInNewTab = (
    dataArray,
    columnTitle,
    employeeName = "",
    showEmployeeName = false,
    filterRange = "",
    formatDateRange = () => "",
    startDate = null,
    endDate = null
  ) => {
    const newTab = window.open();
    let recordsPerPage = 10;
    let currentPage = 1;
    const totalRecords = dataArray.length;

    const formatCallDuration = (duration) => {
      if (!duration) return "00:00:00";
      const [hours, minutes, seconds] = duration.split(":");
      return `${hours}:${minutes}:${seconds ? seconds.split(".")[0] : "00"}`;
    };

    const calculateTotalCallDuration = () => {
      const totalDuration = dataArray.reduce((acc, student) => {
        if (student.call_duration) {
          const [hours, minutes, seconds] = student.call_duration
            .split(":")
            .map((num) => parseInt(num, 10) || 0);
          return acc + (hours * 3600 + minutes * 60 + seconds);
        }
        return acc;
      }, 0);

      const hours = Math.floor(totalDuration / 3600);
      const minutes = Math.floor((totalDuration % 3600) / 60);
      const seconds = totalDuration % 60;

      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    };

    const totalCallDurationFormatted = calculateTotalCallDuration();

    const formatDateDisplay = () => {
      if (!filterRange) return "All Time";
      const today = new Date();
      let displayRange = "";

      switch (filterRange) {
        case "custom":
          if (startDate && endDate) {
            const start = new Date(startDate).toLocaleDateString("en-GB");
            const end = new Date(endDate).toLocaleDateString("en-GB");
            displayRange = `Custom Range: ${start} → ${end}`;
          } else {
            displayRange = "Custom Date Range";
          }
          break;
        case "today": {
          const startToday = new Date(today.setHours(0, 0, 0, 0));
          displayRange = `Today: ${startToday.toLocaleDateString("en-GB")}`;
          break;
        }
        case "yesterday": {
          const startYesterday = new Date(today.setDate(today.getDate() - 1));
          startYesterday.setHours(0, 0, 0, 0);
          displayRange = `Yesterday: ${startYesterday.toLocaleDateString("en-GB")}`;
          break;
        }
        case "this_week": {
          const firstDayOfWeek = new Date(today);
          firstDayOfWeek.setDate(today.getDate() - today.getDay());
          firstDayOfWeek.setHours(0, 0, 0, 0);
          const lastDayOfWeek = new Date(firstDayOfWeek);
          lastDayOfWeek.setDate(firstDayOfWeek.getDate() + 6);
          lastDayOfWeek.setHours(23, 59, 59, 999);
          displayRange = `This Week: ${firstDayOfWeek.toLocaleDateString(
            "en-GB"
          )} → ${lastDayOfWeek.toLocaleDateString("en-GB")}`;
          break;
        }
        case "last_week": {
          const lastWeekDate = new Date(today);
          lastWeekDate.setDate(today.getDate() - today.getDay() - 7);
          lastWeekDate.setHours(0, 0, 0, 0);
          const lastWeekEndDate = new Date(lastWeekDate);
          lastWeekEndDate.setDate(lastWeekDate.getDate() + 6);
          lastWeekEndDate.setHours(23, 59, 59, 999);
          displayRange = `Last Week: ${lastWeekDate.toLocaleDateString(
            "en-GB"
          )} → ${lastWeekEndDate.toLocaleDateString("en-GB")}`;
          break;
        }
        case "this_month": {
          const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          endOfMonth.setHours(23, 59, 59, 999);
          displayRange = `This Month: ${startOfMonth.toLocaleDateString(
            "en-GB"
          )} → ${endOfMonth.toLocaleDateString("en-GB")}`;
          break;
        }
        case "last_month": {
          const startOfLastMonth = new Date(
            today.getFullYear(),
            today.getMonth() - 1,
            1
          );
          const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
          endOfLastMonth.setHours(23, 59, 59, 999);
          displayRange = `Last Month: ${startOfLastMonth.toLocaleDateString(
            "en-GB"
          )} → ${endOfLastMonth.toLocaleDateString("en-GB")}`;
          break;
        }
        default:
          displayRange = "All Time";
          break;
      }
      return displayRange;
    };

    const renderTable = (page, recordsPerPage) => {
      const startIndex = (page - 1) * recordsPerPage;
      const endIndex = Math.min(page * recordsPerPage, totalRecords);
      const currentRecords = dataArray.slice(startIndex, endIndex);

      if (currentRecords.length === 0) {
        return `<tr><td colspan="${showEmployeeName ? 12 : 11}" style="padding: 32px; text-align: center; color: #94a3b8;">No records found for this selection.</td></tr>`;
      }

      return currentRecords
        .map(
          (student) => `
    <tr style="border-bottom: 1px solid #f1f5f9; text-align: left; font-size: 13px; color: #334155;">
      ${showEmployeeName
              ? `<td style="padding: 12px 16px; font-weight: 600; color: #0f172a;">${student.employee_name || "N/A"}</td>`
              : ""
            }
      <td style="padding: 12px 16px; font-weight: 600; color: #2563eb;">${student.enrollment_code || "N/A"}</td>
      <td style="padding: 12px 16px; font-weight: 600; color: #0f172a;">${student.student_name || "N/A"}</td>
      <td style="padding: 12px 16px;">${student.aadhar_number || "N/A"}</td>
      <td style="padding: 12px 16px; max-width: 200px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${student.address || "N/A"}</td>
      <td style="padding: 12px 16px;">${student.city || "N/A"}</td>
      <td style="padding: 12px 16px;">${student.call_status || "N/A"}</td>
      <td style="padding: 12px 16px; font-family: monospace;">${formatCallDuration(student.call_duration)}</td>
      <td style="padding: 12px 16px; font-weight: 600;">${student.is_interested || "N/A"}</td>
      <td style="padding: 12px 16px;">${student.call_back_reason || "N/A"}</td>
      <td style="padding: 12px 16px;">
        <span style="padding: 2px 8px; border-radius: 9999px; font-size: 11px; font-weight: 600; background-color: ${student.is_active === "1" ? "#ecfdf5" : "#fef2f2"
            }; color: ${student.is_active === "1" ? "#10b981" : "#ef4444"};">
          ${student.is_active === "1" ? "Yes" : "No"}
        </span>
      </td>
      <td style="padding: 12px 16px; color: #64748b;">${student.created_date || "N/A"}</td>
    </tr>
  `
        )
        .join("");
    };

    const tableHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${columnTitle} Report</title>
          <script src="https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js"></script>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
              margin: 0;
              padding: 20px;
              background-color: #f8fafc;
              color: #1e293b;
            }
            .header-card {
              background: #ffffff;
              border: 1px solid #e2e8f0;
              border-radius: 12px;
              padding: 16px 20px;
              margin-bottom: 16px;
              display: flex;
              flex-wrap: wrap;
              justify-content: space-between;
              align-items: center;
              gap: 12px;
              box-shadow: 0 1px 2px rgba(0,0,0,0.04);
            }
            .title {
              font-size: 18px;
              font-weight: 700;
              color: #172C57;
              margin: 0;
            }
            .subtitle {
              font-size: 13px;
              color: #64748b;
              margin-top: 4px;
            }
            .badges {
              display: flex;
              align-items: center;
              gap: 10px;
              flex-wrap: wrap;
            }
            .badge {
              padding: 6px 12px;
              border-radius: 8px;
              font-size: 12px;
              font-weight: 600;
            }
            .badge-emerald { background: #ecfdf5; color: #10b981; border: 1px solid #d1fae5; }
            .badge-blue { background: #f0f5ff; color: #2563eb; border: 1px solid #dbeafe; }
            .btn-excel {
              background-color: #10b981;
              color: #ffffff;
              border: 1px solid #059669;
              padding: 6px 14px;
              border-radius: 8px;
              font-size: 12px;
              font-weight: 600;
              cursor: pointer;
              display: inline-flex;
              align-items: center;
              gap: 6px;
              transition: background-color 0.15s ease;
            }
            .btn-excel:hover { background-color: #059669; }
            .table-card {
              background: #ffffff;
              border: 1px solid #e2e8f0;
              border-radius: 12px;
              overflow: hidden;
              box-shadow: 0 1px 2px rgba(0,0,0,0.04);
            }
            table { width: 100%; border-collapse: collapse; }
            th {
              background-color: #f8fafc;
              color: #475569;
              font-size: 12px;
              font-weight: 600;
              text-align: left;
              padding: 12px 16px;
              border-bottom: 1px solid #e2e8f0;
              white-space: nowrap;
            }
            tr:hover { background-color: #f8fafc; }
            .btn {
              padding: 6px 14px;
              border-radius: 6px;
              border: 1px solid #cbd5e1;
              background: #ffffff;
              color: #334155;
              font-size: 12px;
              font-weight: 600;
              cursor: pointer;
            }
            .btn:hover:not(:disabled) { background: #f1f5f9; }
            .btn:disabled { opacity: 0.4; cursor: not-allowed; }
            select {
              padding: 6px 10px;
              border-radius: 6px;
              border: 1px solid #cbd5e1;
              font-size: 12px;
              color: #334155;
              background: #ffffff;
            }
          </style>
        </head>
        <body>
          <div class="header-card">
            <div>
              <h1 class="title">${columnTitle}</h1>
              <div class="subtitle">
                ${employeeName ? `Employee: <strong>${employeeName}</strong> • ` : ""}
                ${formatDateDisplay()}
              </div>
            </div>
            <div class="badges">
              <span class="badge badge-blue">Total Records: ${totalRecords}</span>
              <span class="badge badge-emerald">Total Call Duration: ${totalCallDurationFormatted}</span>
              <button class="btn-excel" onclick="exportDataToExcel()">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export Excel
              </button>
            </div>
          </div>

          <div class="table-card">
            <div style="overflow-x: auto;">
              <table>
                <thead>
                  <tr>
                    ${showEmployeeName ? "<th>Employee Name</th>" : ""}
                    <th>Enrollment Code</th>
                    <th>Full Name</th>
                    <th>Aadhar Number</th>
                    <th>Address</th>
                    <th>City</th>
                    <th>Call Status</th>
                    <th>Call Duration</th>
                    <th>Interest Status</th>
                    <th>Call Back Reason</th>
                    <th>Active</th>
                    <th>Created Date</th>
                  </tr>
                </thead>
                <tbody id="table-body">
                  ${renderTable(currentPage, recordsPerPage)}
                </tbody>
                <tfoot style="background-color: #f8fafc; border-top: 2px solid #e2e8f0; font-size: 13px;">
                  <tr>
                    <td colspan="${showEmployeeName ? 7 : 6}" style="padding: 12px 16px; font-weight: 700; color: #172C57;">
                      Total Call Duration (${totalRecords} Records)
                    </td>
                    <td style="padding: 12px 16px; font-family: monospace; font-weight: 700; color: #10b981;">
                      ${totalCallDurationFormatted}
                    </td>
                    <td colspan="4" style="padding: 12px 16px;"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; background: #f8fafc; border-top: 1px solid #e2e8f0; font-size: 12px; color: #475569;">
              <span>Total Records: <strong>${totalRecords}</strong></span>
              <div style="display: flex; align-items: center; gap: 8px;">
                <button id="prev-btn" class="btn" onclick="changePage('prev')" ${currentPage === 1 ? "disabled" : ""}>Previous</button>
                <span id="page-info" style="font-weight: 600; padding: 0 8px;">Page ${currentPage} of ${Math.max(
      1,
      Math.ceil(totalRecords / recordsPerPage)
    )}</span>
                <button id="next-btn" class="btn" onclick="changePage('next')" ${currentPage >= Math.ceil(totalRecords / recordsPerPage) ? "disabled" : ""}>Next</button>
              </div>
              <div>
                <select id="records-per-page" onchange="changeRecordsPerPage()">
                  <option value="10" ${recordsPerPage === 10 ? "selected" : ""}>10 rows</option>
                  <option value="20" ${recordsPerPage === 20 ? "selected" : ""}>20 rows</option>
                  <option value="40" ${recordsPerPage === 40 ? "selected" : ""}>40 rows</option>
                  <option value="50" ${recordsPerPage === 50 ? "selected" : ""}>50 rows</option>
                  <option value="100" ${recordsPerPage === 100 ? "selected" : ""}>100 rows</option>
                </select>
              </div>
            </div>
          </div>

          <script>
            let currentPage = ${currentPage};
            let recordsPerPage = ${recordsPerPage};
            const totalRecords = ${totalRecords};
            const allRecords = ${JSON.stringify(dataArray)};
            const showEmployeeName = ${showEmployeeName};
            const TOTAL_CALL_DURATION = "${totalCallDurationFormatted}";

            const tableBody = document.getElementById('table-body');
            const pageInfo = document.getElementById('page-info');
            const prevBtn = document.getElementById('prev-btn');
            const nextBtn = document.getElementById('next-btn');

            const formatCallDurationInternal = (duration) => {
              if (!duration) return "00:00:00";
              const [hours, minutes, seconds] = duration.split(":");
              return \`\${hours}:\${minutes}:\${seconds ? seconds.split(".")[0] : "00"}\`;
            };

            function renderTable(page, recordsPerPage) {
              const startIndex = (page - 1) * recordsPerPage;
              const endIndex = Math.min(page * recordsPerPage, totalRecords);
              const currentRecords = allRecords.slice(startIndex, endIndex);

              return currentRecords.map(student => \`
                <tr style="border-bottom: 1px solid #f1f5f9; text-align: left; font-size: 13px; color: #334155;">
                  \${showEmployeeName
        ? \`<td style="padding: 12px 16px; font-weight: 600; color: #0f172a;">\${student.employee_name || "N/A"}</td>\`
        : ""
      }
                  <td style="padding: 12px 16px; font-weight: 600; color: #2563eb;">\${student.enrollment_code || "N/A"}</td>
                  <td style="padding: 12px 16px; font-weight: 600; color: #0f172a;">\${student.student_name || "N/A"}</td>
                  <td style="padding: 12px 16px;">\${student.aadhar_number || "N/A"}</td>
                  <td style="padding: 12px 16px; max-width: 200px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">\${student.address || "N/A"}</td>
                  <td style="padding: 12px 16px;">\${student.city || "N/A"}</td>
                  <td style="padding: 12px 16px;">\${student.call_status || "N/A"}</td>
                  <td style="padding: 12px 16px; font-family: monospace;">\${formatCallDurationInternal(student.call_duration)}</td>
                  <td style="padding: 12px 16px; font-weight: 600;">\${student.is_interested || "N/A"}</td>
                  <td style="padding: 12px 16px;">\${student.call_back_reason || "N/A"}</td>
                  <td style="padding: 12px 16px;">
                    <span style="padding: 2px 8px; border-radius: 9999px; font-size: 11px; font-weight: 600; background-color: \${student.is_active === '1' ? '#ecfdf5' : '#fef2f2'}; color: \${student.is_active === '1' ? '#10b981' : '#ef4444'};">
                      \${student.is_active === '1' ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td style="padding: 12px 16px; color: #64748b;">\${student.created_date || "N/A"}</td>
                </tr>
              \`).join('');
            }

            function updateTable() {
              const totalPages = Math.max(1, Math.ceil(totalRecords / recordsPerPage));
              tableBody.innerHTML = renderTable(currentPage, recordsPerPage);
              pageInfo.textContent = 'Page ' + currentPage + ' of ' + totalPages;
              prevBtn.disabled = currentPage === 1;
              nextBtn.disabled = currentPage >= totalPages;
            }

            function changePage(direction) {
              const totalPages = Math.ceil(totalRecords / recordsPerPage);
              if (direction === 'next' && currentPage < totalPages) currentPage++;
              else if (direction === 'prev' && currentPage > 1) currentPage--;
              updateTable();
            }

            function changeRecordsPerPage() {
              const sel = document.getElementById('records-per-page');
              recordsPerPage = parseInt(sel.value);
              currentPage = 1;
              updateTable();
            }

            function exportDataToExcel() {
              const baseName = "${columnTitle.replace(/[^a-zA-Z0-9_-]/g, "_")}";
              const empPart = "${employeeName ? "_" + employeeName.replace(/[^a-zA-Z0-9_-]/g, "_") : ""}";
              const fileName = baseName + empPart + "_Report";

              if (window.XLSX) {
                const formattedRows = allRecords.map((student) => {
                  const rowObj = {};
                  if (showEmployeeName) rowObj["Employee Name"] = student.employee_name || "N/A";
                  rowObj["Enrollment Code"] = student.enrollment_code || "N/A";
                  rowObj["Full Name"] = student.student_name || "N/A";
                  rowObj["Aadhar Number"] = student.aadhar_number || "N/A";
                  rowObj["Address"] = student.address || "N/A";
                  rowObj["City"] = student.city || "N/A";
                  rowObj["Call Status"] = student.call_status || "N/A";
                  rowObj["Call Duration"] = formatCallDurationInternal(student.call_duration);
                  rowObj["Interest Status"] = student.is_interested || "N/A";
                  rowObj["Call Back Reason"] = student.call_back_reason || "N/A";
                  rowObj["Active"] = student.is_active === "1" ? "Yes" : "No";
                  rowObj["Created Date"] = student.created_date || "N/A";
                  return rowObj;
                });

                /* 👇 TOTAL row with total call duration at the bottom */
                const summaryRow = {};
                if (showEmployeeName) summaryRow["Employee Name"] = "";
                summaryRow["Enrollment Code"] = "";
                summaryRow["Full Name"] = "TOTAL";
                summaryRow["Aadhar Number"] = "";
                summaryRow["Address"] = "";
                summaryRow["City"] = "";
                summaryRow["Call Status"] = "";
                summaryRow["Call Duration"] = TOTAL_CALL_DURATION;
                summaryRow["Interest Status"] = "";
                summaryRow["Call Back Reason"] = "";
                summaryRow["Active"] = "";
                summaryRow["Created Date"] = "";
                formattedRows.push(summaryRow);

                const ws = XLSX.utils.json_to_sheet(formattedRows);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Records");
                XLSX.writeFile(wb, fileName + ".xlsx");
              } else {
                /* CSV fallback (bug fix: Created Date was being dropped) */
                const headersCsv = [
                  showEmployeeName ? "Employee Name" : null,
                  "Enrollment Code",
                  "Full Name",
                  "Aadhar Number",
                  "Address",
                  "City",
                  "Call Status",
                  "Call Duration",
                  "Interest Status",
                  "Call Back Reason",
                  "Active",
                  "Created Date",
                ].filter(Boolean);

                const escapeCSV = (val) => {
                  if (val === null || val === undefined) return '""';
                  return '"' + String(val).replace(/"/g, '""') + '"';
                };

                const csvLines = allRecords.map((student) => {
                  const arr = [];
                  if (showEmployeeName) arr.push(escapeCSV(student.employee_name || "N/A"));
                  arr.push(escapeCSV(student.enrollment_code || "N/A"));
                  arr.push(escapeCSV(student.student_name || "N/A"));
                  arr.push(escapeCSV(student.aadhar_number || "N/A"));
                  arr.push(escapeCSV(student.address || "N/A"));
                  arr.push(escapeCSV(student.city || "N/A"));
                  arr.push(escapeCSV(student.call_status || "N/A"));
                  arr.push(escapeCSV(formatCallDurationInternal(student.call_duration)));
                  arr.push(escapeCSV(student.is_interested || "N/A"));
                  arr.push(escapeCSV(student.call_back_reason || "N/A"));
                  arr.push(escapeCSV(student.is_active === "1" ? "Yes" : "No"));
                  arr.push(escapeCSV(student.created_date || "N/A"));
                  return arr.join(",");
                });

                /* 👇 TOTAL row for CSV */
                const totalArr = [];
                if (showEmployeeName) totalArr.push(escapeCSV(""));
                totalArr.push(escapeCSV(""));
                totalArr.push(escapeCSV("TOTAL"));
                totalArr.push(escapeCSV(""));
                totalArr.push(escapeCSV(""));
                totalArr.push(escapeCSV(""));
                totalArr.push(escapeCSV(""));
                totalArr.push(escapeCSV(TOTAL_CALL_DURATION));
                totalArr.push(escapeCSV(""));
                totalArr.push(escapeCSV(""));
                totalArr.push(escapeCSV(""));
                totalArr.push(escapeCSV(""));
                csvLines.push(totalArr.join(","));

                const csvContent =
                  "\\uFEFF" + [headersCsv.join(","), ...csvLines].join("\\r\\n");
                const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = fileName + ".csv";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }
            }

            updateTable();
          </script>
        </body>
      </html>
    `;

    newTab.document.write(tableHTML);
    newTab.document.close();
  };

  /* ============================================================
     Data fetching
     ============================================================ */
  const fetchEmployeeReport = async () => {
    try {
      const response = await fetch(SummaryApi.employeeReport.url, {
        method: SummaryApi.employeeReport.method,
        headers: headers,
      });

      if (!response.ok) throw new Error("Network response was not ok");
      const result = await response.json();

      if (result.status && result.data) {
        setEmployeeData(result.data);
      } else {
        toast.error("Failed to fetch employee data.");
      }
    } catch (error) {
      toast.error(`Error fetching employee report: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamLeadReport = async (teamLeadId, sDate, eDate) => {
    const formatDateYMD = (d) => {
      if (!d) return "";
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const fromDateStr = sDate ? formatDateYMD(new Date(sDate)) : "";
    const toDateStr = eDate ? formatDateYMD(new Date(eDate)) : "";

    const res = await fetch(SummaryApi.getEmployeesWeeklyReportByTeamLead.url, {
      method: SummaryApi.getEmployeesWeeklyReportByTeamLead.method,
      headers,
      body: JSON.stringify({
        team_lead_emp_id: teamLeadId,
        from_date: fromDateStr,
        to_date: toDateStr,
      }),
    });

    if (!res.ok) throw new Error(`Failed to fetch report for TL ${teamLeadId}`);
    const json = await res.json();
    const rows = Array.isArray(json?.data) ? json.data : [];

    const total = rows.reduce(
      (acc, r) => ({
        total: acc.total + Number(r.total_calls_count || 0),
        interested: acc.interested + Number(r.total_interested_calls_count || 0),
        mostInterested:
          acc.mostInterested +
          Number(r.total_most_interested_calls_count || 0),
        notInterested:
          acc.notInterested + Number(r.total_not_interested_calls_count || 0),
        notAnswered:
          acc.notAnswered + Number(r.total_not_answered_calls_count || 0),
        callDurationSeconds:
          acc.callDurationSeconds +
          Number(
            r.total_call_duration_seconds ||
            r.total_call_duration ||
            0
          ),
      }),
      {
        total: 0,
        interested: 0,
        mostInterested: 0,
        notInterested: 0,
        notAnswered: 0,
        callDurationSeconds: 0,
      }
    );

    return { rows, total };
  };

  const fetchTeamLeads = async () => {
    try {
      const res = await fetch(SummaryApi.getAllTL.url, { method: "GET", headers });
      if (!res.ok) throw new Error("Failed to fetch team leads");
      const json = await res.json();
      if (!json?.status || !Array.isArray(json.data)) {
        throw new Error("Invalid team leads response");
      }
      const tls = json.data.map((tl) => ({
        id: String(tl.employee_id),
        name: tl.full_name,
        memberType: tl.member_type,
        mobile: tl.mobile_number,
        assignedCount: Number(tl.assigned_employee_count || 0),
      }));
      setTeamLeads(tls);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchEmployeeReport();
    fetchTeamLeads();
  }, []);

  useEffect(() => {
    const today = new Date();
    let newStartDate = null;
    let newEndDate = null;

    switch (filterRange) {
      case "today":
        newStartDate = new Date(today.setHours(0, 0, 0, 0));
        newEndDate = new Date(today.setHours(23, 59, 59, 999));
        break;
      case "yesterday":
        newStartDate = new Date(today.setDate(today.getDate() - 1));
        newStartDate.setHours(0, 0, 0, 0);
        newEndDate = new Date(today);
        newEndDate.setHours(23, 59, 59, 999);
        break;
      case "this_week": {
        const firstDayOfWeek = new Date(today);
        firstDayOfWeek.setDate(today.getDate() - today.getDay());
        firstDayOfWeek.setHours(0, 0, 0, 0);
        newStartDate = firstDayOfWeek;

        const lastDayOfWeek = new Date(firstDayOfWeek);
        lastDayOfWeek.setDate(firstDayOfWeek.getDate() + 6);
        lastDayOfWeek.setHours(23, 59, 59, 999);
        newEndDate = lastDayOfWeek;
        break;
      }
      case "last_week": {
        const lastWeekDate = new Date(today);
        lastWeekDate.setDate(today.getDate() - today.getDay() - 7);
        lastWeekDate.setHours(0, 0, 0, 0);
        newStartDate = lastWeekDate;

        const lastWeekEndDate = new Date(lastWeekDate);
        lastWeekEndDate.setDate(lastWeekDate.getDate() + 6);
        lastWeekEndDate.setHours(23, 59, 59, 999);
        newEndDate = lastWeekEndDate;
        break;
      }
      case "this_month":
        newStartDate = new Date(today.getFullYear(), today.getMonth(), 1);
        newEndDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        newEndDate.setHours(23, 59, 59, 999);
        break;
      case "last_month":
        newStartDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        newEndDate = new Date(today.getFullYear(), today.getMonth(), 0);
        newEndDate.setHours(23, 59, 59, 999);
        break;
      case "custom":
        break;
      default:
        newStartDate = null;
        newEndDate = null;
        break;
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
  }, [filterRange]);

  const columns = React.useMemo(
    () => [
      { Header: "S.No", accessor: "sno" },
      { Header: "Employee Name", accessor: "full_name" },
      { Header: "Mobile Number", accessor: "mobile_number" },
      {
        Header: "Total Calls",
        accessor: "students_count",
        Cell: ({ value }) => (
          <span
            className={`inline-flex items-center justify-center min-w-[36px] px-2.5 py-1 rounded-full text-xs font-bold ${value > 0
              ? "bg-[#F0F5FF] text-[#2563EB] border border-[#DBEAFE]"
              : "text-slate-400"
              }`}
          >
            {value}
          </span>
        ),
      },
      {
        Header: "Interested",
        accessor: "interested_count",
        Cell: ({ value }) => (
          <span
            className={`inline-flex items-center justify-center min-w-[36px] px-2.5 py-1 rounded-full text-xs font-bold ${value > 0
              ? "bg-[#ECFDF5] text-[#10B981] border border-[#D1FAE5]"
              : "text-slate-400"
              }`}
          >
            {value}
          </span>
        ),
      },
      {
        Header: "Most Interested",
        accessor: "most_interested_count",
        Cell: ({ value }) => (
          <span
            className={`inline-flex items-center justify-center min-w-[36px] px-2.5 py-1 rounded-full text-xs font-bold ${value > 0
              ? "bg-[#F5F3FF] text-[#7C3AED] border border-[#EDE9FE]"
              : "text-slate-400"
              }`}
          >
            {value}
          </span>
        ),
      },
      {
        Header: "Aadhar Count",
        accessor: "aadhar_count",
        Cell: ({ value }) => (
          <span
            className={`inline-flex items-center justify-center min-w-[36px] px-2.5 py-1 rounded-full text-xs font-semibold ${value > 0
              ? "bg-slate-100 text-slate-800 border border-slate-200"
              : "text-slate-400"
              }`}
          >
            {value}
          </span>
        ),
      },
      {
        Header: "Not Answered",
        accessor: "not_answered_count",
        Cell: ({ value }) => (
          <span
            className={`inline-flex items-center justify-center min-w-[36px] px-2.5 py-1 rounded-full text-xs font-bold ${value > 0
              ? "bg-[#FEF2F2] text-[#EF4444] border border-[#FEE2E2]"
              : "text-slate-400"
              }`}
          >
            {value}
          </span>
        ),
      },
      {
        Header: "Call Back",
        accessor: "call_back_count",
        Cell: ({ value }) => (
          <span
            className={`inline-flex items-center justify-center min-w-[36px] px-2.5 py-1 rounded-full text-xs font-bold ${value > 0
              ? "bg-[#FFFBEB] text-[#D97706] border border-[#FEF3C7]"
              : "text-slate-400"
              }`}
          >
            {value}
          </span>
        ),
      },
      {
        Header: "Not Interested",
        accessor: "not_working_count",
        Cell: ({ value }) => (
          <span
            className={`inline-flex items-center justify-center min-w-[36px] px-2.5 py-1 rounded-full text-xs font-semibold ${value > 0
              ? "bg-rose-50 text-rose-600 border border-rose-200"
              : "text-slate-400"
              }`}
          >
            {value}
          </span>
        ),
      },
    ],
    []
  );

  const filterStudentsByDate = (students) => {
    if (!Array.isArray(students)) return [];
    if (startDate && endDate) {
      return students.filter((student) => {
        const studentCallDate = new Date(student.created_date);
        studentCallDate.setHours(0, 0, 0, 0);
        return studentCallDate >= startDate && studentCallDate <= endDate;
      });
    }
    return students;
  };

  const data = React.useMemo(
    () =>
      employeeData.map((employee, index) => {
        const filteredStudents = filterStudentsByDate(employee.students);

        const interestedStudents = filteredStudents.filter(
          (student) => student.is_interested === "Interested"
        );
        const mostInterestedStudents = filteredStudents.filter(
          (student) => student.is_interested === "Most Interested"
        );
        const aadharStudents = filteredStudents.filter((student) =>
          isValidAadharDate(student.aadhar_number)
        );
        const notAnsweredStudents = filteredStudents.filter(
          (student) => student.call_status === "Not Answered"
        );
        const callBackStudents = filteredStudents.filter(
          (student) => student.call_back_reason !== "N/A"
        );
        const notWorkingStudents = filteredStudents.filter(
          (student) => student.is_interested === "Not Interested"
        );

        return {
          ...employee,
          sno: index + 1,
          students: filteredStudents,
          interestedStudents,
          mostInterestedStudents,
          aadharStudents,
          notAnsweredStudents,
          callBackStudents,
          notWorkingStudents,
          students_count: filteredStudents.length,
          interested_count: interestedStudents.length,
          most_interested_count: mostInterestedStudents.length,
          aadhar_count: aadharStudents.length,
          not_answered_count: notAnsweredStudents.length,
          call_back_count: callBackStudents.length,
          not_working_count: notWorkingStudents.length,
        };
      }),
    [employeeData, startDate, endDate, filterRange]
  );

  const calculateTotals = () => {
    return data.reduce(
      (totals, employee) => {
        totals.students_count += employee.students_count;
        totals.interested_count += employee.interested_count;
        totals.most_interested_count += employee.most_interested_count;
        totals.aadhar_count += employee.aadhar_count;
        totals.not_answered_count += employee.not_answered_count;
        totals.call_back_count += employee.call_back_count;
        totals.not_working_count += employee.not_working_count;

        totals.students.push(...employee.students);
        totals.interested.push(...employee.interestedStudents);
        totals.most_interested.push(...employee.mostInterestedStudents);
        totals.aadhar.push(...employee.aadharStudents);
        totals.not_answered.push(...employee.notAnsweredStudents);
        totals.call_back.push(...employee.callBackStudents);
        totals.not_working.push(...employee.notWorkingStudents);

        return totals;
      },
      {
        students_count: 0,
        interested_count: 0,
        most_interested_count: 0,
        aadhar_count: 0,
        not_answered_count: 0,
        call_back_count: 0,
        not_working_count: 0,
        students: [],
        interested: [],
        most_interested: [],
        aadhar: [],
        not_answered: [],
        call_back: [],
        not_working: [],
      }
    );
  };

  const totals = React.useMemo(() => calculateTotals(), [data]);

  /* ============================================================
     Excel export via ExcelJS
     ============================================================ */
  const handleExportToExcel = async (target) => {
    try {
      setExporting(true);
      const currentDate = new Date();

      const fileDate = (startDate || currentDate)
        .toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
        .replace(/\s+/g, "_");

      const reportPeriodText = formatDateRange();

      const workbook = new ExcelJS.Workbook();

      if (target === "all") {
        const worksheet = workbook.addWorksheet("Employee Report");

        /* Row 1: title */
        const titleRow = worksheet.addRow([
          "OAKYA IT SERVICES PVT LTD, SANJIVANI 2026-27",
        ]);
        titleRow.font = { bold: true, size: 18, underline: true };
        titleRow.alignment = { horizontal: "center", vertical: "center" };
        worksheet.mergeCells("A1:J1");

        /* Row 2: period */
        const dateRow = worksheet.addRow([`REPORT PERIOD: ${reportPeriodText}`]);
        dateRow.font = { bold: true, italic: true, underline: true, size: 13 };
        dateRow.alignment = { horizontal: "center", vertical: "center" };
        worksheet.mergeCells("A2:J2");

        worksheet.addRow([]);

        const excelHeaders = [
          "Sno",
          "Employee Name",
          "Mobile Number",
          "Total Calls",
          "Interested Count",
          "Most Interested Count",
          "Aadhar Count",
          "Not Answered Calls Count",
          "Call Back Count",
          "Not Working Count",
        ];

        const headerRow = worksheet.addRow(excelHeaders);
        headerRow.eachCell((cell) => {
          cell.font = { bold: true };
          cell.alignment = { horizontal: "center", vertical: "middle" };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "ADD8E6" },
          };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });

        const exportData = data.map((row) => [
          row.sno,
          row.full_name,
          row.mobile_number,
          row.students_count,
          row.interested_count,
          row.most_interested_count,
          row.aadhar_count,
          row.not_answered_count,
          row.call_back_count,
          row.not_working_count,
        ]);

        exportData.forEach((row) => {
          const dataRow = worksheet.addRow(row);
          dataRow.eachCell((cell) => {
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          });
        });

        /* Existing totals row */
        const footerRow = worksheet.addRow([
          "Total",
          "",
          "",
          totals.students_count,
          totals.interested_count,
          totals.most_interested_count,
          totals.aadhar_count,
          totals.not_answered_count,
          totals.call_back_count,
          totals.not_working_count,
        ]);
        footerRow.eachCell((cell) => {
          cell.alignment = { horizontal: "center", vertical: "middle" };
          cell.font = { bold: true };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });

        /* 👇 NEW: Total Call Duration summary row at the bottom */
        const totalCallDuration = sumCallDurations(totals.students);
        const durationRow = worksheet.addRow([
          "Total Call Duration",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          totalCallDuration,
        ]);
        durationRow.eachCell((cell) => {
          cell.alignment = { horizontal: "center", vertical: "middle" };
          cell.font = { bold: true, color: { argb: "FF10B981" } };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
        /* Merge label across A..I so the duration value sits in J */
        worksheet.mergeCells(
          `A${durationRow.number}:I${durationRow.number}`
        );
        durationRow.getCell(1).alignment = {
          horizontal: "center",
          vertical: "middle",
        };

        worksheet.columns.forEach((column) => {
          column.width = column.header ? Math.max(column.header.length + 2, 14) : 14;
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `Employee_Task_Report_${fileDate}.xlsx`);
        toast.success("All employees report exported successfully!");
      } else {
        const selectedTL = teamLeads.find((tl) => tl.id === target);
        const tlName = selectedTL ? selectedTL.name : "Team";

        const { rows, total } = await fetchTeamLeadReport(
          target,
          startDate,
          endDate
        );

        const worksheet = workbook.addWorksheet(`${tlName} Team Report`);

        const titleRow = worksheet.addRow([
          `OAKYA IT SERVICES PVT LTD, SANJIVANI 2026-27 : ${tlName.toUpperCase()} TEAM REPORT`,
        ]);
        titleRow.font = { bold: true, size: 16, underline: true };
        titleRow.alignment = { horizontal: "center", vertical: "center" };
        worksheet.mergeCells("A1:G1");

        const dateRow = worksheet.addRow([`REPORT PERIOD: ${reportPeriodText}`]);
        dateRow.font = { bold: true, italic: true, size: 12 };
        dateRow.alignment = { horizontal: "center", vertical: "center" };
        worksheet.mergeCells("A2:G2");

        worksheet.addRow([]);

        const excelHeaders = [
          "Sno",
          "Employee Name",
          "Total Calls",
          "Interested",
          "Most Interested",
          "Not Interested",
          "Not Answered",
        ];

        const headerRow = worksheet.addRow(excelHeaders);
        headerRow.eachCell((cell) => {
          cell.font = { bold: true };
          cell.alignment = { horizontal: "center", vertical: "middle" };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "E0E7FF" },
          };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });

        if (rows.length === 0) {
          const emptyRow = worksheet.addRow([
            "No data available for this team in the selected period",
          ]);
          worksheet.mergeCells(`A${emptyRow.number}:G${emptyRow.number}`);
          emptyRow.alignment = { horizontal: "center" };
        } else {
          rows.forEach((r, idx) => {
            const dataRow = worksheet.addRow([
              idx + 1,
              r.employee_name || r.full_name || "N/A",
              Number(r.total_calls_count || 0),
              Number(r.total_interested_calls_count || 0),
              Number(r.total_most_interested_calls_count || 0),
              Number(r.total_not_interested_calls_count || 0),
              Number(r.total_not_answered_calls_count || 0),
            ]);
            dataRow.eachCell((cell) => {
              cell.alignment = { horizontal: "center", vertical: "middle" };
              cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
              };
            });
          });

          const footerRow = worksheet.addRow([
            "Total",
            "",
            total.total,
            total.interested,
            total.mostInterested,
            total.notInterested,
            total.notAnswered,
          ]);
          footerRow.eachCell((cell) => {
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.font = { bold: true };
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          });

          /* 👇 NEW: Total Call Duration row for team report */
          let totalDurationSeconds = total.callDurationSeconds || 0;
          if (!totalDurationSeconds) {
            totalDurationSeconds = rows.reduce((acc, r) => {
              if (r.total_call_duration && typeof r.total_call_duration === "string") {
                const [h, m, s] = r.total_call_duration
                  .split(":")
                  .map((n) => parseInt(n, 10) || 0);
                return acc + h * 3600 + m * 60 + s;
              }
              return acc + Number(r.total_call_duration_seconds || 0);
            }, 0);
          }

          const tH = Math.floor(totalDurationSeconds / 3600);
          const tM = Math.floor((totalDurationSeconds % 3600) / 60);
          const tS = totalDurationSeconds % 60;
          const totalCallDurationStr = `${String(tH).padStart(
            2,
            "0"
          )}:${String(tM).padStart(2, "0")}:${String(tS).padStart(2, "0")}`;

          const durationRow = worksheet.addRow([
            "Total Call Duration",
            "",
            "",
            "",
            "",
            "",
            totalCallDurationStr,
          ]);
          durationRow.eachCell((cell) => {
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.font = { bold: true, color: { argb: "FF10B981" } };
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          });
          worksheet.mergeCells(`A${durationRow.number}:F${durationRow.number}`);
        }

        worksheet.columns.forEach((column) => {
          column.width = 16;
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(
          new Blob([buffer]),
          `Team_Report_${tlName.replace(/\s+/g, "_")}_${fileDate}.xlsx`
        );
        toast.success(`${tlName} Team report exported!`);
      }

      setShowExportModal(false);
    } catch (err) {
      toast.error(`Export failed: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data });

  const clearFilters = () => {
    setFilterRange("");
    setStartDate(null);
    setEndDate(null);
  };

  const formatDateRange = () => {
    if (filterRange === "custom" && startDate && endDate) {
      const formatCustom = (d) =>
        `${String(d.getDate()).padStart(2, "0")}-${String(
          d.getMonth() + 1
        ).padStart(2, "0")}-${d.getFullYear()}`;
      return `${formatCustom(startDate)} → ${formatCustom(endDate)}`;
    }

    let rangeDescription = "";
    const today = new Date();
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    const formatDate = (date) => {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    switch (filterRange) {
      case "today":
        rangeDescription = `Today (${formatDate(today)})`;
        break;
      case "yesterday": {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        rangeDescription = `Yesterday (${formatDate(yesterday)})`;
        break;
      }
      case "this_week":
      case "last_week":
      case "this_month":
      case "last_month":
        rangeDescription = `${formatDate(start)} → ${formatDate(end)}`;
        break;
      default:
        rangeDescription = "All Time";
    }

    return rangeDescription;
  };

  return (
    <div className="min-h-screen space-y-4 text-slate-800">
      {loading ? (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-30 flex justify-center items-center">
          <div className="bg-white px-6 py-4 rounded-xl shadow-lg flex items-center gap-3">
            <div
              className="animate-spin rounded-full h-6 w-6 border-b-2"
              style={{ borderColor: THEME.primary }}
            ></div>
            <span className="text-slate-700 text-sm font-semibold">
              Fetching Report Data...
            </span>
          </div>
        </div>
      ) : (
        <>
          {/* Top Header & Unified Filter Bar */}
          <div className="bg-white rounded-xl border border-slate-200 px-5 py-4 shadow-sm space-y-3.5">
            <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
              <div className="flex items-center gap-3.5">
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center text-white shadow-sm shrink-0"
                  style={{ backgroundColor: THEME.primary }}
                >
                  <FaPhoneAlt size={17} />
                </div>
                <div>
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <h1
                      className="text-lg font-bold tracking-tight"
                      style={{ color: THEME.primary }}
                    >
                      Employee Task Report
                    </h1>
                    {filterRange && (
                      <span
                        style={{
                          backgroundColor: THEME.primaryLight,
                          color: THEME.primary,
                        }}
                        className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold"
                      >
                        <FaCalendarAlt size={10} />
                        {formatDateRange()}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Overview of total calls and student interest status across employees.
                  </p>
                </div>
              </div>

              {/* Controls */}
              <div className="flex flex-wrap items-center gap-2.5 w-full xl:w-auto justify-start xl:justify-end">
                <div className="flex items-center gap-2 bg-[#F8FAFC] border border-slate-200 rounded-lg px-3 py-1.5">
                  <span className="text-xs font-medium text-slate-500">Period:</span>
                  <select
                    onChange={(e) => setFilterRange(e.target.value)}
                    value={filterRange}
                    className="bg-transparent text-xs font-semibold text-slate-800 focus:outline-none cursor-pointer"
                  >
                    <option value="">All Time</option>
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="this_week">This Week</option>
                    <option value="last_week">Last Week</option>
                    <option value="this_month">This Month</option>
                    <option value="last_month">Last Month</option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>

                <button
                  onClick={clearFilters}
                  className="px-3 py-1.5 bg-[#FEF2F2] hover:bg-[#FEE2E2] text-[#EF4444] border border-[#FECACA] rounded-lg text-xs font-semibold transition-colors"
                >
                  Clear Filters
                </button>

                <button
                  onClick={() => fetchEmployeeReport()}
                  style={{ backgroundColor: THEME.primary }}
                  className="flex items-center gap-1.5 px-3.5 py-1.5 text-white rounded-lg text-xs font-medium hover:opacity-90 transition-opacity shadow-sm"
                  title="Refresh Data"
                >
                  <FaSyncAlt size={11} />
                  Refresh
                </button>

                <button
                  onClick={() => setShowExportModal(true)}
                  disabled={exporting}
                  className="flex items-center gap-1.5 px-3.5 py-1.5 bg-[#10B981] hover:bg-[#059669] disabled:opacity-50 text-white rounded-lg text-xs font-medium transition-colors shadow-sm"
                >
                  <FaFileExcel size={13} />
                  Export Excel
                </button>
              </div>
            </div>

            {/* Custom Date Picker Row */}
            {filterRange === "custom" && (
              <div className="pt-3 border-t border-slate-100 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-semibold text-slate-600">
                    Start Date:
                  </label>
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    className="px-3 py-1.5 border border-slate-200 bg-[#F8FAFC] focus:bg-white rounded-lg text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#172C57]"
                    dateFormat="yyyy/MM/dd"
                    maxDate={endDate}
                    isClearable
                    placeholderText="Select Start Date"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <label className="text-xs font-semibold text-slate-600">
                    End Date:
                  </label>
                  <DatePicker
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    className="px-3 py-1.5 border border-slate-200 bg-[#F8FAFC] focus:bg-white rounded-lg text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#172C57] disabled:opacity-50"
                    dateFormat="yyyy/MM/dd"
                    minDate={startDate}
                    isClearable
                    placeholderText="Select End Date"
                    disabled={!startDate}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Employee Wise Call Summary Table Card */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3.5 border-b border-slate-100 flex justify-between items-center">
              <h2
                className="text-sm font-bold"
                style={{ color: THEME.primary }}
              >
                Employee Wise Call Summary
              </h2>
              <span className="text-xs text-slate-500">
                Click any count badge to view detailed records
              </span>
            </div>

            <div className="overflow-x-auto">
              <table
                {...getTableProps()}
                className="min-w-full divide-y divide-slate-100"
              >
                <thead className="bg-[#F8FAFC]">
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th
                          {...column.getHeaderProps()}
                          className="px-4 py-3 text-left text-xs font-semibold text-slate-600 tracking-wide whitespace-nowrap"
                        >
                          {column.render("Header")}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody
                  {...getTableBodyProps()}
                  className="divide-y divide-slate-100 bg-white"
                >
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="text-center py-8 text-sm text-slate-400"
                      >
                        No employee records found.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => {
                      prepareRow(row);
                      return (
                        <tr
                          {...row.getRowProps()}
                          className="hover:bg-slate-50/80 transition-colors"
                        >
                          {row.cells.map((cell) => {
                            const clickableEffect =
                              "cursor-pointer hover:scale-105 transition-transform duration-150";

                            if (cell.column.id === "students_count") {
                              return (
                                <td
                                  {...cell.getCellProps()}
                                  className={`px-4 py-2.5 text-xs ${clickableEffect}`}
                                  onClick={() =>
                                    openInNewTab(
                                      row.original.students,
                                      "TOTAL CALLS",
                                      row.original.full_name,
                                      false,
                                      filterRange,
                                      formatDateRange,
                                      startDate,
                                      endDate
                                    )
                                  }
                                >
                                  {cell.render("Cell")}
                                </td>
                              );
                            }
                            if (cell.column.id === "interested_count") {
                              return (
                                <td
                                  {...cell.getCellProps()}
                                  className={`px-4 py-2.5 text-xs ${clickableEffect}`}
                                  onClick={() =>
                                    openInNewTab(
                                      row.original.interestedStudents,
                                      "INTERESTED STUDENTS",
                                      row.original.full_name,
                                      false,
                                      filterRange,
                                      formatDateRange,
                                      startDate,
                                      endDate
                                    )
                                  }
                                >
                                  {cell.render("Cell")}
                                </td>
                              );
                            }
                            if (cell.column.id === "most_interested_count") {
                              return (
                                <td
                                  {...cell.getCellProps()}
                                  className={`px-4 py-2.5 text-xs ${clickableEffect}`}
                                  onClick={() =>
                                    openInNewTab(
                                      row.original.mostInterestedStudents,
                                      "MOST INTERESTED STUDENTS",
                                      row.original.full_name,
                                      false,
                                      filterRange,
                                      formatDateRange,
                                      startDate,
                                      endDate
                                    )
                                  }
                                >
                                  {cell.render("Cell")}
                                </td>
                              );
                            }
                            if (cell.column.id === "aadhar_count") {
                              return (
                                <td
                                  {...cell.getCellProps()}
                                  className={`px-4 py-2.5 text-xs ${clickableEffect}`}
                                  onClick={() =>
                                    openInNewTab(
                                      row.original.aadharStudents,
                                      "AADHAR STUDENTS",
                                      row.original.full_name,
                                      false,
                                      filterRange,
                                      formatDateRange,
                                      startDate,
                                      endDate
                                    )
                                  }
                                >
                                  {cell.render("Cell")}
                                </td>
                              );
                            }
                            if (cell.column.id === "not_answered_count") {
                              return (
                                <td
                                  {...cell.getCellProps()}
                                  className={`px-4 py-2.5 text-xs ${clickableEffect}`}
                                  onClick={() =>
                                    openInNewTab(
                                      row.original.notAnsweredStudents,
                                      "NOT ANSWERED STUDENTS",
                                      row.original.full_name,
                                      false,
                                      filterRange,
                                      formatDateRange,
                                      startDate,
                                      endDate
                                    )
                                  }
                                >
                                  {cell.render("Cell")}
                                </td>
                              );
                            }
                            if (cell.column.id === "call_back_count") {
                              return (
                                <td
                                  {...cell.getCellProps()}
                                  className={`px-4 py-2.5 text-xs ${clickableEffect}`}
                                  onClick={() =>
                                    openInNewTab(
                                      row.original.callBackStudents,
                                      "CALL BACK STUDENTS",
                                      row.original.full_name,
                                      false,
                                      filterRange,
                                      formatDateRange,
                                      startDate,
                                      endDate
                                    )
                                  }
                                >
                                  {cell.render("Cell")}
                                </td>
                              );
                            }
                            if (cell.column.id === "not_working_count") {
                              return (
                                <td
                                  {...cell.getCellProps()}
                                  className={`px-4 py-2.5 text-xs ${clickableEffect}`}
                                  onClick={() =>
                                    openInNewTab(
                                      row.original.notWorkingStudents,
                                      "NOT WORKING STUDENTS",
                                      row.original.full_name,
                                      false,
                                      filterRange,
                                      formatDateRange,
                                      startDate,
                                      endDate
                                    )
                                  }
                                >
                                  {cell.render("Cell")}
                                </td>
                              );
                            }
                            if (cell.column.id === "full_name") {
                              return (
                                <td
                                  {...cell.getCellProps()}
                                  className="px-4 py-2.5 text-xs font-semibold text-slate-900 whitespace-nowrap"
                                >
                                  {cell.render("Cell")}
                                </td>
                              );
                            }

                            return (
                              <td
                                {...cell.getCellProps()}
                                className="px-4 py-2.5 text-xs text-slate-600 whitespace-nowrap"
                              >
                                {cell.render("Cell")}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
                <tfoot className="bg-[#F8FAFC] border-t-2 border-slate-200">
                  <tr>
                    <td
                      colSpan={3}
                      className="px-4 py-3 font-bold text-xs uppercase tracking-wider"
                      style={{ color: THEME.primary }}
                    >
                      Total Summary
                    </td>

                    <td className="px-4 py-3 text-xs" onClick={() => openInNewTab(totals.students, "TOTAL STUDENTS", "", true, filterRange, formatDateRange, startDate, endDate)}>
                      <span className="inline-flex items-center justify-center min-w-[36px] px-2.5 py-1 rounded-full text-xs font-bold bg-[#F0F5FF] text-[#2563EB] border border-[#DBEAFE] cursor-pointer hover:opacity-80">
                        {totals.students_count}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-xs" onClick={() => openInNewTab(totals.interested, "INTERESTED STUDENTS", "", true, filterRange, formatDateRange, startDate, endDate)}>
                      <span className="inline-flex items-center justify-center min-w-[36px] px-2.5 py-1 rounded-full text-xs font-bold bg-[#ECFDF5] text-[#10B981] border border-[#D1FAE5] cursor-pointer hover:opacity-80">
                        {totals.interested_count}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-xs" onClick={() => openInNewTab(totals.most_interested, "MOST INTERESTED STUDENTS", "", true, filterRange, formatDateRange, startDate, endDate)}>
                      <span className="inline-flex items-center justify-center min-w-[36px] px-2.5 py-1 rounded-full text-xs font-bold bg-[#F5F3FF] text-[#7C3AED] border border-[#EDE9FE] cursor-pointer hover:opacity-80">
                        {totals.most_interested_count}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-xs" onClick={() => openInNewTab(totals.aadhar, "AADHAR STUDENTS", "", true, filterRange, formatDateRange)}>
                      <span className="inline-flex items-center justify-center min-w-[36px] px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-800 border border-slate-200 cursor-pointer hover:opacity-80">
                        {totals.aadhar_count}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-xs" onClick={() => openInNewTab(totals.not_answered, "NOT ANSWERED STUDENTS", "", true, filterRange, formatDateRange, startDate, endDate)}>
                      <span className="inline-flex items-center justify-center min-w-[36px] px-2.5 py-1 rounded-full text-xs font-bold bg-[#FEF2F2] text-[#EF4444] border border-[#FEE2E2] cursor-pointer hover:opacity-80">
                        {totals.not_answered_count}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-xs" onClick={() => openInNewTab(totals.call_back, "CALL BACK STUDENTS", "", true, filterRange, formatDateRange, startDate, endDate)}>
                      <span className="inline-flex items-center justify-center min-w-[36px] px-2.5 py-1 rounded-full text-xs font-bold bg-[#FFFBEB] text-[#D97706] border border-[#FEF3C7] cursor-pointer hover:opacity-80">
                        {totals.call_back_count}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-xs" onClick={() => openInNewTab(totals.not_working, "NOT WORKING STUDENTS", "", true, filterRange, formatDateRange, startDate, endDate)}>
                      <span className="inline-flex items-center justify-center min-w-[36px] px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-600 border border-rose-200 cursor-pointer hover:opacity-80">
                        {totals.not_working_count}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Export Selection Modal */}
          {showExportModal && (
            <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-slate-200">
                <div
                  className="px-6 py-4 flex items-center justify-between text-white"
                  style={{ backgroundColor: THEME.primary }}
                >
                  <div className="flex items-center gap-2.5">
                    <FaFileExcel size={18} />
                    <h3 className="font-bold text-base">Select Export Target</h3>
                  </div>
                  <button
                    onClick={() => {
                      if (!exporting) setShowExportModal(false);
                    }}
                    disabled={exporting}
                    className="p-1.5 hover:bg-white/10 rounded-full transition-colors text-white/80 hover:text-white disabled:opacity-50"
                  >
                    <FaTimes size={14} />
                  </button>
                </div>

                <div className="p-6 space-y-4">
                  <p className="text-xs text-slate-600">
                    Choose whether to export the report for <strong>All Employees</strong> or a specific <strong>Team Lead</strong>.
                  </p>

                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-slate-700">
                      Export Option:
                    </label>
                    <select
                      value={selectedExportTeam}
                      onChange={(e) => setSelectedExportTeam(e.target.value)}
                      disabled={exporting}
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:bg-white cursor-pointer transition-all disabled:opacity-50"
                    >
                      <option value="all">All Employees</option>
                      {teamLeads.map((tl) => (
                        <option key={tl.id} value={tl.id}>
                          {tl.name} Team
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="pt-2 flex items-center justify-end gap-3">
                    <button
                      type="button"
                      disabled={exporting}
                      onClick={() => setShowExportModal(false)}
                      className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors border border-slate-200 disabled:opacity-50"
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      disabled={exporting}
                      onClick={() => handleExportToExcel(selectedExportTeam)}
                      className="px-5 py-2 text-xs font-semibold text-white bg-[#10B981] hover:bg-[#059669] rounded-lg transition-colors shadow-sm flex items-center gap-2 disabled:opacity-60"
                    >
                      <FaDownload size={12} />
                      {exporting ? "Exporting..." : "Download Excel"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmployeeReport;